import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../services/db";
import {
  CheckCircle,
  XCircle,
  Loader,
  KeyRound,
  Clock,
  AlertTriangle,
  History,
  Eye,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  addSecurityLog,
  getAllLogs,
  getSecurityData,
  verifySecurityAnswer,
} from "../services/Queries/AdminQueries";
import axios from "axios";
import { useAuths } from "../context/AuthProviders";

const PromoteToHR = () => {
  const { userId } = useParams();
  const { user: currentUser, isAuthenticated } = useAuths();

  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [securityData, setSecurityData] = useState(null);
  const [answer, setAnswer] = useState("");
  const [showQuestion, setShowQuestion] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);

  // First check authentication
  useEffect(() => {
    const checkAuth = async () => {
      // Check if anyone is logged in
      if (!isAuthenticated) {
        window.location.href = `/login?redirect=/admin/promote-to-hr/${userId}`;
        return;
      }

      // Check if logged-in user is admin or hr
      if (
        currentUser?.profile?.user_type !== "admin" &&
        currentUser?.profile?.user_type !== "hr"
      ) {
        setError("You must be an admin or HR to promote users");
        setLoading(false);
        return;
      }

      // If admin is logged in, proceed to load data
      loadData();
    };

    checkAuth();
  }, [userId, isAuthenticated, currentUser]);

  const loadData = async () => {
    if (!userId) {
      setError("Invalid user ID");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const {
        data: users,
        error: userError,
        status,
        statusText,
      } = await supabase.from("profiles").select("*").eq("id", userId);

      console.log("Query response:", {
        status,
        statusText,
        users,
        error: userError,
      });
      if (userError) {
        console.error("Error fetching user:", userError);
        throw userError;
      }

      // Debug info
      console.log("=== DEBUG INFO ===");
      console.log("URL User ID:", userId);
      console.log("Users found:", users?.length || 0);

      // Check if user exists
      if (!users || users.length === 0) {
        setError("User not found in database");
        setLoading(false);
        return;
      }

      // Get the first user
      const userProfile = users[0];
      setUserData(userProfile);

      console.log("User data from DB:", userProfile);
      console.log("User type:", userProfile?.user_type);
      console.log("User email:", userProfile?.email);
      console.log("=================");

      // 2. Fetch security question and logs
      const securityResult = await getSecurityData();

      if (!securityResult.success) {
        throw new Error(
          securityResult.error || "Failed to fetch security data",
        );
      }

      setSecurityData(securityResult.data);

      // Parse logs
      try {
        const logs = JSON.parse(securityResult.data.logs || "[]");
        setActivityLogs(logs);
      } catch {
        setActivityLogs([]);
      }

      // If already HR, show message
      if (userProfile.user_type === "hr" || userProfile.user_type === "admin") {
        await addLogEntry("page_view", "info", {
          message: "User already has HR role",
        });

        setResult({
          success: true,
          message: "User is already an HR member",
          user: userProfile,
        });
        setLoading(false);
        return;
      }

      // Log page view
      await addLogEntry("page_view", "success", {
        message: "Promotion page loaded",
      });

      setShowQuestion(true);
      setLoading(false);
    } catch (err) {
      console.error("Error loading data:", err);
      setError(err.message || "Failed to load data");
      setLoading(false);
    }
  };

  // Function to add log entry
  const addLogEntry = async (action, status, details = {}) => {
    try {
      const logEntry = {
        action,
        status,
        user: userData?.email || "unknown",
        userId: userId,
        details: {
          ...details,
          timestamp: new Date().toISOString(),
        },
      };

      const result = await addSecurityLog(logEntry);

      if (result.success) {
        setActivityLogs(result.logs);
      }
    } catch (err) {
      console.error("Failed to add log:", err);
    }
  };

  const verifyAnswer = async () => {
    if (!answer.trim()) {
      toast.error("Please enter the security answer");
      return;
    }

    setVerifying(true);

    try {
      // Log attempt
      await addLogEntry("answer_attempt", "pending", {
        answerLength: answer.length,
      });

      // Verify answer using API
      const verification = await verifySecurityAnswer(answer);

      if (!verification.success) {
        throw new Error(verification.error);
      }

      if (!verification.isCorrect) {
        // Log wrong answer
        await addLogEntry("wrong_answer", "failed", {
          message: "Incorrect security answer provided",
        });

        toast.error("Incorrect security answer");
        setVerifying(false);
        return;
      }

      // Log correct answer
      await addLogEntry("correct_answer", "success", {
        message: "Security verification passed",
      });

      // Proceed with promotion
      await promoteUser();
    } catch (err) {
      console.error("Verification error:", err);

      await addLogEntry("verification_error", "failed", {
        error: err.message,
      });

      toast.error("Failed to verify answer");
      setVerifying(false);
    }
  };

  const promoteUser = async () => {
  try {
    await addLogEntry("promotion_start", "pending");

    // Update user role to HR in profiles table
    const { error: updateError } = await supabase
      .from("profiles")
      .update({ user_type: "hr" })
      .eq("id", userId);

    if (updateError) throw updateError;

    // Fetch the updated profile - WITHOUT .single() to avoid PGRST116 error
    const { data: updatedProfiles, error: fetchError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId);

    if (fetchError) {
      console.log('Fetch error:', fetchError);
      // Create a fallback user object
      const fallbackUser = {
        id: userId,
        email: userData?.email || 'unknown',
        user_type: 'hr'
      };
      
      setResult({
        success: true,
        message: "User successfully promoted to HR!",
        user: fallbackUser,
        timestamp: new Date().toISOString(),
      });
    } else {
      // Get the first profile from the array
      const updatedProfile = updatedProfiles && updatedProfiles.length > 0 
        ? updatedProfiles[0] 
        : { 
            id: userId, 
            email: userData?.email || 'unknown', 
            user_type: 'hr' 
          };
      
      setResult({
        success: true,
        message: "User successfully promoted to HR!",
        user: updatedProfile,
        timestamp: new Date().toISOString(),
      });
    }

    // Send approval email
    if (userData && userData.email) {
      try {
        const formData = new FormData();
        formData.append("email", userData.email);
        formData.append("name", userData.first_name || "User");
        formData.append("userId", userId);

        const response = await axios.post(
          "https://samahomecare.com/notifyUser.php",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        if (response.data.success) {
          toast.success("Approval email sent to the user!");
        } else {
          console.error("Failed to send approval email:", response.data);
        }
      } catch (error) {
        console.log("Error Sending Email: ", error);
      }
    } else {
      console.log("User data not available for email:", { userData, userId });
    }

    toast.success("User promoted to HR successfully!");
    setShowQuestion(false);

    // Refresh logs
    const logsResult = await getAllLogs();
    if (logsResult.success) {
      setActivityLogs(logsResult.logs);
    }
  } catch (err) {
    console.error("Promotion error:", err);

    // Log failure
    await addLogEntry("promotion_failed", "failed", {
      error: err.message,
    });

    setError(err.message || "Failed to update user role");
    toast.error("Failed to promote user: " + err.message);
  } finally {
    setVerifying(false);
  }
};

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "bg-green-100 text-green-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "info":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "promotion_success":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "promotion_failed":
        return <XCircle className="w-4 h-4 text-red-600" />;
      case "wrong_answer":
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case "correct_answer":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "page_view":
        return <Eye className="w-4 h-4 text-blue-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <Loader className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading...</h2>
          <p className="text-gray-600">
            Please wait while we verify the details
          </p>
        </div>
      </div>
    );
  }

  // Security Question Screen
  if (showQuestion && securityData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-center">
              <KeyRound className="w-16 h-16 text-white mx-auto" />
            </div>

            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">
                Security Verification
              </h2>
              <p className="text-gray-600 text-center mb-6">
                Please answer the security question to proceed
              </p>

              {/* User Info Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">User:</span>
                    <span className="font-medium text-gray-800">
                      {userData?.email}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Current Role:</span>
                    <span className="bg-gray-200 px-3 py-1 rounded-full text-xs font-medium">
                      {userData?.user_type || "user"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">New Role:</span>
                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
                      HR
                    </span>
                  </div>
                </div>
              </div>

              {/* Security Question */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {securityData.question}
                </label>
                <input
                  type="password"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="Enter your answer"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === "Enter" && verifyAnswer()}
                  autoFocus
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={verifyAnswer}
                  disabled={verifying}
                  className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {verifying ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    "Verify & Promote"
                  )}
                </button>
                <a
                  href="/"
                  className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-center"
                >
                  Cancel
                </a>
              </div>

              {/* View Logs Button */}
              {activityLogs.length > 0 && (
                <button
                  onClick={() => setShowLogs(!showLogs)}
                  className="mt-4 w-full flex items-center justify-center gap-2 text-sm text-purple-600 hover:text-purple-800"
                >
                  <History className="w-4 h-4" />
                  {showLogs
                    ? "Hide Activity Logs"
                    : `View Activity Logs (${activityLogs.length})`}
                </button>
              )}
            </div>
          </div>

          {/* Logs Section */}
          {showLogs && activityLogs.length > 0 && (
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <History className="w-5 h-5 text-purple-600" />
                  Activity History
                </h3>
              </div>
              <div className="p-6 max-h-96 overflow-y-auto">
                <div className="space-y-3">
                  {[...activityLogs].reverse().map((log, index) => (
                    <div
                      key={log.id || index}
                      className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                    >
                      <div className="mt-1">{getActionIcon(log.action)}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-800 capitalize">
                            {log.action?.replace(/_/g, " ") || "Unknown"}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(log.status)}`}
                          >
                            {log.status || "unknown"}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">
                          {formatDate(log.timestamp)}
                        </p>
                        {log.details?.message && (
                          <p className="text-xs text-gray-600 mt-1">
                            {log.details.message}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="bg-red-500 p-6 text-center">
            <XCircle className="w-16 h-16 text-white mx-auto" />
          </div>
          <div className="p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Access Denied
            </h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <a
              href="/"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Homepage
            </a>
          </div>
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Powered by{" "}
              <a
                href="https://triodevelopers.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-600 hover:text-purple-800 font-medium"
              >
                Trio Developers
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success State
  if (result?.success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Success Card */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-center">
              <CheckCircle className="w-16 h-16 text-white mx-auto" />
            </div>
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">
                {result.message}
              </h2>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium text-gray-800">
                      {result.user.email}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Previous Role:</span>
                    <span className="font-medium text-gray-800">
                      {userData?.user_type || "user"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">New Role:</span>
                    <span className="font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">
                      HR
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Promoted At:</span>
                    <span className="text-sm text-gray-800">
                      {formatDate(result.timestamp)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <a
                  href="/"
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-center"
                >
                  Go to Home
                </a>
              </div>

              {/* View Logs Button */}
              {activityLogs.length > 0 && (
                <button
                  onClick={() => setShowLogs(!showLogs)}
                  className="mt-4 w-full flex items-center justify-center gap-2 text-sm text-green-600 hover:text-green-800"
                >
                  <History className="w-4 h-4" />
                  {showLogs
                    ? "Hide Activity Logs"
                    : `View Activity Logs (${activityLogs.length})`}
                </button>
              )}
            </div>
          </div>

          {/* Logs Section */}
          {showLogs && activityLogs.length > 0 && (
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <History className="w-5 h-5 text-green-600" />
                  Complete Activity History
                </h3>
              </div>
              <div className="p-6 max-h-96 overflow-y-auto">
                <div className="space-y-3">
                  {[...activityLogs].reverse().map((log, index) => (
                    <div
                      key={log.id || index}
                      className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                    >
                      <div className="mt-1">{getActionIcon(log.action)}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-800 capitalize">
                            {log.action?.replace(/_/g, " ") || "Unknown"}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(log.status)}`}
                          >
                            {log.status || "unknown"}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">
                          {formatDate(log.timestamp)}
                        </p>
                        {log.details?.message && (
                          <p className="text-xs text-gray-600 mt-1">
                            {log.details.message}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              Powered by{" "}
              <a
                href="https://triodevelopers.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-600 hover:text-green-800 font-medium"
              >
                Trio Developers
              </a>{" "}
              • All actions are logged for security
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PromoteToHR;
